import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
ChangeDetectionStrategy,Component,ElementRef,
EventEmitter,HostBinding,inject,Input,OnDestroy,Optional,Output,Self
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { MatLegacyFormField as MatFormField,MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Subject,Subscription } from 'rxjs';
import { compareArrays, isNotNullOrUndefined } from '../helper/helper';
import { MathJsCustomScopeService } from '../services/math-js-custom-scope.service';
import { CalculationDialogComponent,CalculationDialogComponentData,CalculationDialogComponentReturnData } from './calculation-dialog/calculation-dialog.component';

@Component({
    selector: 'app-calculation-input',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatInputModule, FontAwesomeModule, MatTooltipModule, MatBadgeModule, FormsModule],
    templateUrl: './calculation-input.component.html',
    styleUrls: ['./calculation-input.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: CalculationInputComponent }, MathJsCustomScopeService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalculationInputComponent implements OnDestroy, ControlValueAccessor, MatFormFieldControl<number> {
    calculationContextService = inject(MathJsCustomScopeService, { optional: true, skipSelf: true }) ?? inject(MathJsCustomScopeService);

    result: number | null = null;

    @Input() useVariables = false;

    @Input() maxDecimalPlaces = 0;

    _formula: string;
    @Input()
    set formula(s: string) {
        this._formula = s;
    }
    @Output() formulaChange = new EventEmitter<string>();

    _formulaUsedVariables: string[] = [];
    @Input()
    set formulaUsedVariables(s: string[]) {
        this._formulaUsedVariables = s;
    }

    @Output() formulaUsedVariablesChange = new EventEmitter<string[]>();

    controlType = 'calculation-input';
    static nextId = 0;
    stateChanges = new Subject<void>();

    focused = false;
    touched = false;

    onTouched = () => {};
    onChange: (v: number | null) => void = () => {};

    subs: Subscription[] = [];

    @HostBinding() id = `app-calculation-input-${CalculationInputComponent.nextId++}`;
    @Input()
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(plh) {
        this._placeholder = plh;
        this.stateChanges.next();
    }
    private _placeholder: string;

    @Input()
    get value(): number | null {
        return this.result;
    }
    set value(v: number | null) {
        this.result = v;
        // todo formel is now invalid
        this.stateChanges.next();
    }
    constructor(@Optional() @Self() public ngControl: NgControl, @Optional() public parentFormField: MatFormField, private _elementRef: ElementRef<HTMLElement>, private dialog: MatLegacyDialog) {
        // Replace the provider from above with this.
        if (this.ngControl != null) {
            // Setting the value accessor directly (instead of using
            // the providers) to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
    }

    onFocusIn(event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }
    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }
    get empty() {
        return !isNotNullOrUndefined(this.result);
    }
    @Input()
    get required() {
        return this._required;
    }
    set required(req) {
        this._required = coerceBooleanProperty(req);
        this.stateChanges.next();
    }
    private _required = false;
    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _disabled = false;

    get errorState(): boolean {
        return false;
    }
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('aria-describedby') userAriaDescribedBy: string;
    setDescribedByIds(ids: string[]) {
        const controlElement = this._elementRef.nativeElement.querySelector('.app-calculation-input-container')!;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }
    onContainerClick(event: MouseEvent) {
        if ((event.target as Element).tagName.toLowerCase() != 'input') {
            this._elementRef.nativeElement.querySelector('input').focus();
        }
    }
    writeValue(tel: number | null): void {
        this.value = tel;
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Sets the disabled state of the input field.
     * REQUIRED for formControl usage
     * @param isDisabled True to disable the input, false to enable it.
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
        this.subs.forEach((s) => s.unsubscribe());
    }

    onValueChange(v: number | null) {
        this.value = v;
        this.onChange(v);
    }
    onCalculatorClick() {
        this.dialog
            .open<CalculationDialogComponent, CalculationDialogComponentData, CalculationDialogComponentReturnData>(CalculationDialogComponent, {
                ...CalculationDialogComponent.DefaultConfig,
                data: {
                    initialFormula: this._formula,
                    initialResult: this.value,
                    maxDecimalPlaces: this.maxDecimalPlaces,
                    disabled: this.disabled,
                    calculationContextService: this.useVariables ? this.calculationContextService : null,
                },
            })
            .afterClosed()
            .subscribe((ret) => {
                if (ret) {
                    if (ret.formular) {
                        this.onValueChange(ret.resultRounded);
                    }
                    this._formula = ret.formular;
                    this.formulaChange.emit(this._formula);
                    if (!compareArrays(ret.usedVariables, this._formulaUsedVariables)) {
                        this.formulaUsedVariablesChange.emit(ret.usedVariables);
                    }
                }
            });
    }
}
